import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const headers = () => { const t = localStorage.getItem("cauldron_token"); return t ? { Authorization: `Bearer ${t}` } : {}; };

/**
 * Admin-only Hot Rewards Wallet dispersal console.
 * Route: /admin/rewards
 */
export default function AdminRewards() {
  const [audiences, setAudiences] = useState(null);
  const [runs, setRuns] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [form, setForm] = useState({ audience: "elders", caulPerUser: 100, reason: "", userIds: "", cadence: "" });
  const [sending, setSending] = useState(false);
  const [lastRun, setLastRun] = useState(null);

  const load = async () => {
    try {
      const [a, h, s] = await Promise.all([
        axios.get(`${API}/admin/rewards/audiences`, { headers: headers() }),
        axios.get(`${API}/admin/rewards/history`, { headers: headers() }),
        axios.get(`${API}/admin/rewards/schedules`, { headers: headers() }),
      ]);
      setAudiences(a.data);
      setRuns(h.data.runs || []);
      setSchedules(s.data.schedules || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    }
  };
  useEffect(() => { load(); }, []);

  const disperse = async () => {
    const audienceCount = form.audience === "custom"
      ? form.userIds.split(",").filter(Boolean).length
      : (audiences?.[form.audience] || 0);
    if (audienceCount === 0) return toast.error("Audience has 0 recipients");
    // If a cadence is picked, create a schedule instead of running now.
    if (form.cadence) {
      try {
        await axios.post(`${API}/admin/rewards/schedule`, {
          audience: form.audience,
          caulPerUser: parseInt(form.caulPerUser),
          reason: form.reason || `Recurring ${form.cadence}`,
          cadence: form.cadence,
        }, { headers: headers() });
        toast.success(`Schedule created — runs every ${form.cadence}`);
        setForm({ ...form, cadence: "" });
        load();
      } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
      return;
    }
    const total = form.caulPerUser * audienceCount;
    if (!window.confirm(`Send ${form.caulPerUser} CAUL × ${audienceCount} recipients = ${total} CAUL total from the Hot Rewards Wallet. Confirm?`)) return;
    setSending(true);
    try {
      const payload = {
        audience: form.audience,
        caulPerUser: parseInt(form.caulPerUser),
        reason: form.reason || `Airdrop ${new Date().toISOString().slice(0,10)}`,
      };
      if (form.audience === "custom") {
        payload.userIds = form.userIds.split(",").map(s => s.trim()).filter(Boolean);
      }
      const r = await axios.post(`${API}/admin/rewards/disperse`, payload, { headers: headers(), timeout: 300000 });
      setLastRun(r.data);
      toast.success(`Dispersed to ${r.data.successCount} of ${r.data.recipientsCount} recipients`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Dispersal failed");
    } finally { setSending(false); }
  };

  const cancelSchedule = async (sid) => {
    if (!window.confirm("Cancel this recurring schedule?")) return;
    try {
      await axios.delete(`${API}/admin/rewards/schedule/${sid}`, { headers: headers() });
      toast.success("Schedule cancelled");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto p-6" data-testid="admin-rewards">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 rounded-full bg-amber-950/40 border border-amber-500/30">
            <Sparkles className="h-5 w-5 text-amber-400" />
          </div>
          <h1 className="text-3xl font-serif text-amber-100">Hot Rewards Wallet</h1>
        </div>
        <p className="text-sm text-amber-200/60 mb-6">
          Disperse CAUL from the platform's on-chain hot wallet to every eligible member's linked XRPL address.
        </p>

        {/* Audience counts */}
        {audiences && (
          <div className="grid grid-cols-3 gap-3 mb-6">
            {[
              ["all_trustline", "All trustline members"],
              ["founding_ambassadors", "Founding Ambassadors"],
              ["elders", "Elders"],
            ].map(([k, label]) => (
              <div key={k} className="rounded-lg border border-neutral-800 bg-neutral-900/40 p-3" data-testid={`audience-${k}`}>
                <div className="text-xs text-amber-200/60 uppercase tracking-wider">{label}</div>
                <div className="text-2xl font-serif text-amber-50">{audiences[k]}</div>
              </div>
            ))}
          </div>
        )}

        {/* Form */}
        <div className="rounded-lg border border-amber-500/40 bg-neutral-900/60 p-5 space-y-4 mb-6">
          <div>
            <label className="text-xs text-amber-200/60 uppercase tracking-wider">Audience</label>
            <select
              value={form.audience}
              onChange={e => setForm({ ...form, audience: e.target.value })}
              className="mt-1 w-full px-3 py-2 rounded-md bg-neutral-950 border border-neutral-700 text-amber-100"
              data-testid="audience-select"
            >
              <option value="elders">Elders</option>
              <option value="founding_ambassadors">Founding Ambassadors</option>
              <option value="all_trustline">All trustline members</option>
              <option value="custom">Custom userIds…</option>
            </select>
            {audiences && form.audience !== "custom" && (
              <div className="mt-1 text-xs text-amber-200/60" data-testid="audience-count">
                Audience: {audiences[form.audience] || 0} eligible recipients
              </div>
            )}
          </div>

          {form.audience === "custom" && (
            <div>
              <label className="text-xs text-amber-200/60 uppercase tracking-wider">Comma-separated user IDs</label>
              <Textarea
                value={form.userIds}
                onChange={e => setForm({ ...form, userIds: e.target.value })}
                placeholder="uuid1, uuid2, …"
                rows={3}
                data-testid="custom-userids"
              />
            </div>
          )}

          <div>
            <label className="text-xs text-amber-200/60 uppercase tracking-wider">CAUL per recipient</label>
            <Input
              type="number"
              min="1"
              value={form.caulPerUser}
              onChange={e => setForm({ ...form, caulPerUser: parseInt(e.target.value) || 100 })}
              data-testid="caul-per-user"
            />
          </div>

          <div>
            <label className="text-xs text-amber-200/60 uppercase tracking-wider">Reason (memo)</label>
            <Input
              value={form.reason}
              onChange={e => setForm({ ...form, reason: e.target.value })}
              placeholder="Founding Airdrop 2026"
              data-testid="disperse-reason"
            />
          </div>

          <div>
            <label className="text-xs text-amber-200/60 uppercase tracking-wider">Repeat (optional)</label>
            <select
              value={form.cadence}
              onChange={e => setForm({ ...form, cadence: e.target.value })}
              className="mt-1 w-full px-3 py-2 rounded-md bg-neutral-950 border border-neutral-700 text-amber-100"
              data-testid="cadence-select"
            >
              <option value="">One-off (run now)</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>

          <Button
            onClick={disperse}
            disabled={sending}
            className="w-full bg-amber-500 hover:bg-amber-400 text-neutral-900"
            data-testid="disperse-btn"
          >
            {sending ? "Dispersing on XRPL…" : form.cadence ? `Schedule ${form.cadence} airdrop` : "Disperse now"}
          </Button>
        </div>

        {/* Active schedules */}
        {schedules.length > 0 && (
          <div className="mb-6">
            <h2 className="text-xl font-serif text-amber-100 mb-3">Active schedules</h2>
            <ul className="space-y-2" data-testid="schedules-list">
              {schedules.filter(s => s.status === "active").map(s => (
                <li key={s.id} className="p-3 rounded-lg border border-amber-500/30 bg-amber-950/20 text-sm flex justify-between items-start" data-testid={`schedule-${s.id}`}>
                  <div>
                    <div className="text-amber-100">
                      <b>{s.cadence}</b> · {s.caulPerUser} CAUL × {s.audience}
                    </div>
                    <div className="text-xs text-amber-200/60 mt-1">
                      {s.reason} · next run: {new Date(s.nextRunAt).toLocaleString()}
                      {s.runCount > 0 && ` · ${s.runCount} run${s.runCount===1?"":"s"} completed`}
                    </div>
                  </div>
                  <button
                    onClick={() => cancelSchedule(s.id)}
                    className="text-red-400 hover:text-red-300 text-xs"
                    data-testid={`cancel-schedule-${s.id}`}
                  >Cancel</button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Last run result */}
        {lastRun && (
          <div className="rounded-lg border border-emerald-800/40 bg-emerald-950/20 p-4 mb-6" data-testid="last-run">
            <h3 className="text-emerald-300 font-serif mb-2">Run complete</h3>
            <div className="text-sm text-emerald-100/80">
              {lastRun.successCount} of {lastRun.recipientsCount} paid ·
              {lastRun.errorCount > 0 && <span className="text-red-400"> {lastRun.errorCount} errored</span>}
            </div>
            {(lastRun.results || []).slice(0, 5).map((r, i) => (
              <div key={i} className="text-xs text-emerald-100/60 mt-1 font-mono">
                {r.name || r.userId?.slice(0,8)} · {r.status} · {r.txHash?.slice(0,10) || r.error?.slice(0,60)}
              </div>
            ))}
          </div>
        )}

        {/* History */}
        <h2 className="text-xl font-serif text-amber-100 mb-3">Recent runs</h2>
        {runs.length === 0 ? (
          <div className="text-amber-200/50 text-sm py-4 text-center">No previous dispersals.</div>
        ) : (
          <ul className="space-y-2" data-testid="runs-history">
            {runs.map(r => (
              <li key={r.id} className="p-3 rounded-lg border border-neutral-800 bg-neutral-900/40 text-sm">
                <div className="flex justify-between">
                  <div className="text-amber-100">{r.reason || "Untitled"}</div>
                  <div className="text-xs text-amber-200/50">{new Date(r.startedAt).toLocaleString()}</div>
                </div>
                <div className="text-xs text-amber-200/60 mt-1">
                  {r.audience} · {r.caulPerUser} CAUL × {r.recipientsCount} · {r.successCount}/{r.recipientsCount} success
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Layout>
  );
}
